/**
 * IP アドレス・CIDR の包含判定（画面表示用。判定の正は API の lib/ip.ts）。
 * IP 制限画面で「現在の接続元がこのルールに一致するか」を表示するために使う。
 */
type Parsed = { family: 4 | 6; value: bigint };

function parseV4(s: string): bigint | null {
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  let v = 0n;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p) || Number(p) > 255) return null;
    v = (v << 8n) | BigInt(Number(p));
  }
  return v;
}

export function parseIp(input: string | null | undefined): Parsed | null {
  if (!input) return null;
  let s = input.trim().replace(/^\[|\]$/g, '').replace(/%.*$/, '');
  if (!s.includes(':')) {
    const v = parseV4(s);
    return v == null ? null : { family: 4, value: v };
  }
  let tail: bigint | null = null;
  const last = s.lastIndexOf(':');
  if (s.slice(last + 1).includes('.')) {
    tail = parseV4(s.slice(last + 1));
    if (tail == null) return null;
    s = `${s.slice(0, last + 1)}0:0`;
  }
  const dbl = s.split('::');
  if (dbl.length > 2) return null;
  const left = dbl[0] ? dbl[0].split(':') : [];
  const right = dbl.length === 2 && dbl[1] ? dbl[1].split(':') : [];
  const groups = dbl.length === 2 ? [...left, ...Array<string>(8 - left.length - right.length).fill('0'), ...right] : left;
  if (groups.length !== 8 || groups.some((g) => !/^[0-9a-fA-F]{1,4}$/.test(g))) return null;
  let v = groups.reduce((acc, g) => (acc << 16n) | BigInt(parseInt(g, 16)), 0n);
  if (tail != null) v = (v & ~0xffffffffn) | tail;
  if (v >> 32n === 0xffffn) return { family: 4, value: v & 0xffffffffn };
  return { family: 6, value: v };
}

/** CIDR（または単一 IP）に IP が含まれるか */
export function cidrContains(cidr: string, ip: string | null | undefined): boolean {
  const [addr, pfx] = cidr.split('/');
  const net = parseIp(addr);
  const a = parseIp(ip);
  if (!net || !a || net.family !== a.family) return false;
  const bits = net.family === 4 ? 32 : 128;
  const prefix = pfx == null ? bits : Number(pfx);
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > bits) return false;
  const mask = prefix === 0 ? 0n : ((1n << BigInt(prefix)) - 1n) << BigInt(bits - prefix);
  return (a.value & mask) === (net.value & mask);
}
